import React, { useState, useEffect } from 'react';
import FormInput from '../common/FormInput';
import FormButton from '../common/FormButton';
import SportsInterestSection from './SportsInterestSection';
import { authService } from '../../../services/api';

const ProfileForm = ({ onNavigate, onUserUpdate }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    gender: '',
    age: '',
    country: '',
    city: '',
    bio: '',
    date_of_birth: '',
    website: '',
    linkedin: '',
    twitter: '',
    instagram: '',
    profile_picture: null
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [profilePicturePreview, setProfilePicturePreview] = useState(null);
  const [profileMeta, setProfileMeta] = useState({
    society: null,
    email: '',
    interestedInSports: false,
    city: '',
  });

  useEffect(() => {
    loadUserProfile();
  }, []);

  const loadUserProfile = async () => {
    setLoading(true);
    try {
      const profile = await authService.getUserProfile();
      setProfileMeta({
        society: profile.society || null,
        email: profile.email || '',
        interestedInSports: Boolean(profile.interested_in_sports),
        city: profile.city || '',
      });
      setFormData({
        first_name: profile.first_name || '',
        last_name: profile.last_name || '',
        gender: profile.gender || '',
        age: profile.age || '',
        country: profile.country || '',
        city: profile.city || '',
        bio: profile.bio || '',
        date_of_birth: profile.date_of_birth || '',
        website: profile.website || '',
        linkedin: profile.linkedin || '',
        twitter: profile.twitter || '',
        instagram: profile.instagram || '',
        profile_picture: profile.profile_picture || null
      });
      
      // Set profile picture preview if it exists
      if (profile.profile_picture) {
        setProfilePicturePreview(profile.profile_picture);
      }
    } catch (error) {
      setErrors({ general: error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setErrors(prev => ({
          ...prev,
          profile_picture: 'Please select a valid image file'
        }));
        return;
      }
      
      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        setErrors(prev => ({
          ...prev,
          profile_picture: 'File size must be less than 5MB'
        }));
        return;
      }
      
      setFormData(prev => ({
        ...prev,
        profile_picture: file
      }));
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setProfilePicturePreview(e.target.result);
      };
      reader.readAsDataURL(file);
      
      // Clear error
      if (errors.profile_picture) {
        setErrors(prev => ({
          ...prev,
          profile_picture: ''
        }));
      }
    } else {
      // If no file selected, clear the profile_picture field
      setFormData(prev => ({
        ...prev,
        profile_picture: null
      }));
      setProfilePicturePreview(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErrors({});
    setMessage('');
    
    try {
      // Create FormData for file upload
      const submitData = new FormData();
      
      // Add all form fields
      Object.keys(formData).forEach(key => {
        if (key === 'profile_picture') {
          // Handle profile picture specially
          if (formData[key] instanceof File) {
            // User selected a new file
            submitData.append(key, formData[key]);
          } else if (formData[key] === null) {
            // User wants to remove the profile picture
            submitData.append(key, '');
          }
          // If formData[key] is undefined or empty string, don't append anything
        } else if (formData[key] !== null && formData[key] !== '') {
          // For non-file fields, convert to string
          submitData.append(key, String(formData[key]));
        }
      });
      
      await authService.updateUserProfile(submitData);
      setMessage('Profile updated successfully!');
      setTimeout(() => setMessage(''), 3000);
      if (onUserUpdate) {
        onUserUpdate();
      }
    } catch (error) {
      if (error.type === 'validation_error') {
        setErrors(error.errors);
      } else {
        setErrors({ general: error.message });
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">Loading your profile...</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <form onSubmit={handleSubmit} className="space-y-8" encType="multipart/form-data">
        {errors.general && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
            {errors.general}
          </div>
        )}

        {message && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md">
            {message}
          </div>
        )}

        {/* Profile Picture */}
        <div className="bg-gray-50 p-6 rounded-lg">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Profile Picture</h3>
          <div className="flex items-center space-x-6">
            <div className="flex-shrink-0">
              {profilePicturePreview ? (
                <img
                  className="h-24 w-24 rounded-full object-cover border-4 border-white shadow-lg"
                  src={profilePicturePreview}
                  alt="Profile preview"
                />
              ) : (
                <div className="h-24 w-24 rounded-full bg-gray-300 flex items-center justify-center border-4 border-white shadow-lg">
                  <svg className="h-12 w-12 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                  </svg>
                </div>
              )}
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Upload Profile Picture
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
              {profilePicturePreview && (
                <button
                  type="button"
                  onClick={() => {
                    setFormData(prev => ({ ...prev, profile_picture: null }));
                    setProfilePicturePreview(null);
                  }}
                  className="mt-2 text-sm text-red-600 hover:text-red-800 font-medium"
                >
                  Remove Picture
                </button>
              )}
              {errors.profile_picture && (
                <p className="mt-1 text-sm text-red-600">{errors.profile_picture}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                JPG, PNG or GIF. Max size 5MB.
              </p>
            </div>
          </div>
        </div>

        {/* Basic Information */}
        <div className="bg-gray-50 p-6 rounded-lg">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Basic Information</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <FormInput
              label="First Name"
              name="first_name"
              value={formData.first_name}
              onChange={handleChange}
              placeholder="Enter your first name"
              error={errors.first_name}
            />

            <FormInput
              label="Last Name"
              name="last_name"
              value={formData.last_name}
              onChange={handleChange}
              placeholder="Enter your last name"
              error={errors.last_name}
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Gender
              </label>
              <select
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
                <option value="prefer_not_to_say">Prefer not to say</option>
              </select>
              {errors.gender && (
                <p className="mt-1 text-sm text-red-600">{errors.gender}</p>
              )}
            </div>

            <FormInput
              label="Age"
              type="number"
              name="age"
              value={formData.age}
              onChange={handleChange}
              placeholder="Enter your age"
              error={errors.age}
              min="13"
              max="120"
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Country
              </label>
              <select
                name="country"
                value={formData.country}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select country</option>
                <option value="Pakistan">Pakistan</option>
                <option value="United States">United States</option>
                <option value="United Kingdom">United Kingdom</option>
                <option value="Canada">Canada</option>
                <option value="Australia">Australia</option>
                <option value="Germany">Germany</option>
                <option value="France">France</option>
                <option value="India">India</option>
                <option value="Bangladesh">Bangladesh</option>
                <option value="Afghanistan">Afghanistan</option>
                <option value="Iran">Iran</option>
                <option value="Turkey">Turkey</option>
                <option value="Saudi Arabia">Saudi Arabia</option>
                <option value="UAE">United Arab Emirates</option>
                <option value="other">Other</option>
              </select>
              {errors.country && (
                <p className="mt-1 text-sm text-red-600">{errors.country}</p>
              )}
            </div>

            <FormInput
              label="City"
              name="city"
              value={formData.city}
              onChange={handleChange}
              placeholder="Enter your city"
              error={errors.city}
            />
          </div>
        </div>

        <SportsInterestSection
          userEmail={profileMeta.email}
          society={profileMeta.society}
          city={formData.city || profileMeta.city || ''}
          interestedInSports={profileMeta.interestedInSports}
          onUpdated={loadUserProfile}
        />

        {/* Bio Section */}
        <div className="bg-gray-50 p-6 rounded-lg">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">About You</h3>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Bio
            </label>
            <textarea
              name="bio"
              value={formData.bio}
              onChange={handleChange}
              placeholder="Tell us about yourself..."
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            {errors.bio && (
              <p className="mt-1 text-sm text-red-600">{errors.bio}</p>
            )}
          </div>

          <div className="mt-6">
            <FormInput
              label="Date of Birth"
              type="date"
              name="date_of_birth"
              value={formData.date_of_birth}
              onChange={handleChange}
              error={errors.date_of_birth}
            />
          </div>
        </div>

        {/* Social Media Links */}
        <div className="bg-gray-50 p-6 rounded-lg">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Social Media Links</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <FormInput
              label="Website"
              type="url"
              name="website"
              value={formData.website}
              onChange={handleChange}
              placeholder="https://yourwebsite.com"
              error={errors.website}
            />

            <FormInput
              label="LinkedIn"
              type="url"
              name="linkedin"
              value={formData.linkedin}
              onChange={handleChange}
              placeholder="https://linkedin.com/in/yourprofile"
              error={errors.linkedin}
            />

            <FormInput
              label="Twitter"
              type="url"
              name="twitter"
              value={formData.twitter}
              onChange={handleChange}
              placeholder="https://twitter.com/yourhandle"
              error={errors.twitter}
            />

            <FormInput
              label="Instagram"
              type="url"
              name="instagram"
              value={formData.instagram}
              onChange={handleChange}
              placeholder="https://instagram.com/yourhandle"
              error={errors.instagram}
            />
          </div>
        </div>

        <div className="flex justify-end">
          <FormButton
            type="submit"
            loading={saving}
            disabled={saving}
            className="px-8 py-3"
          >
            Update Profile
          </FormButton>
        </div>
      </form>
    </div>
  );
};

export default ProfileForm;
